// @ts-strict-ignore
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { RouteComponentProps, withRouter } from 'react-router'
import { Route as ReactRouterDomRoute, Redirect } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { IonLabel, IonRouterOutlet, IonTabBar, IonTabButton, IonTabs, IonPage } from '@ionic/react'
import classNames from 'classnames'

import { Helmet } from 'react-helmet'

import {
	HomeContainer as Container,
	// TODO: replace props
	ReadyGateContainer,
	AppMetaData,
	Language,
	convertMetaDataToTabConfig,
	TabConfig,
	ScreenNavigationParams,
	CollectionListNavigationParams,
	COLLECTION_ITEM_SUBTYPE_TOUR,
	COLLECTION_ITEM_SUBTYPE_LIST,
	COLLECTION_ITEM_SUBTYPE_STORY,
	COLLECTION_ITEM_SUBTYPE_WEB,
	COLLECTION_ITEM_SUBTYPE_MENU,
	COLLECTION_ITEM_SUBTYPE_PANORAMA,
	AppMetaDataTab,
	CodeStatus,
	convertLanguage,
} from 'mytours-read-common'

import {
	BASE_PATH,
	getScannerUrl,
	getSettingsUrl,
	getProfileUrl,
	SCANNER_PAGE_SLUG,
	SETTINGS_PAGE_SLUG,
	PROFILE_PAGE_SLUG,
	MORE_TAB_SLUG,
	getScannerStackRoutePath,
	getSettingsStackRoutePath,
	getTabsStackRoutePath,
	getCollectionListRoutePath,
	getCollectionTourRoutePath,
	getCollectionItemsRoutePath,
	getScreenStoryRoutePath,
	getScreenWebRoutePath,
	getScreenPanoramaRoutePath,
	getBasePath,
	formatLanguageUrl,
	getTabIndex,
	getTabByIndex,
	getCollectionMenuRoutePath,
	getTabsIDsStackRoutePath,
	formatTabsUrl,
	get404RoutePath,
	matchPaths,
	get404Url,
	getProfileStackRoutePath,
	getBadgesStackRoutePath,
	getFavouritesStackRoutePath,
} from '../../navigation/routes'
import NavigationService from '../../navigation/NavigationService'
import SettingsService from 'mobile_web/modules/settings/functions'
import { RouteProps, saveDefaultLanguageConfig, getPageComponent, tabsCount } from '../functions'

import CollectionListRoute from '../../collection/routes/CollectionListRoute'
import CollectionTourRoute from '../../collection/routes/CollectionTourRoute'
import ScreenStoryRoute from '../../screen/routes/ScreenStoryRoute'
import ScreenWebRoute from '../../screen/routes/ScreenWebRoute'
import ScreenPanoramaRoute from '../../screen/routes/ScreenPanoramaRoute'
import CollectionItemsRoute from '../../collection/routes/CollectionItemsRoute'
import ScannerRoute from '../../scanner/routes/ScannerRoute'
import SettingsRoute from '../../settings/routes/SettingsRoute'
import ProfileRoute from '../../profile/routes/ProfileRoute'
import BadgesRoute from '../../badges/routes/BadgesRoute'
import LockAppRoute from '../../lockapp/routes/LockAppRoute'

import { getDirMode, isIOSSafariBrowser, isPageInsideIFrame, isRTL, isSafariBrowser } from '../../platform'
import strings from '../../../strings'

import '../styles.less'
import { ionHistory } from '../../navigation'
import { validateAppCode } from '../../lockapp/functions'
import { LanguageCode, RedeemCode } from 'mytours-read-api-typescript'
import WithMediaQuery, { MediaQueryProps, UseMediaArgs } from 'mobile_web/modules/common/hocs/media-query'
import Page404 from 'mobile_web/404'
import ScannerIcon from 'mobile_web/modules/scanner/components/parts/ScannerIcon'
import { getScannerLabel } from 'mobile_web/modules/scanner/functions'
import DefaultPagePlaceholder from 'mobile_web/modules/common/components/placeholders/DefaultPage'
import { checkAndremoveFullWidthClass } from 'mobile_web/modules/map/functions'
import MorePopover from '../parts/more-popover'
import TabIcon from '../parts/TabIcon'
import usePrevious from 'common_lib/hooks/use-previous'
import ModalPage from 'mobile_web/modules/common/components/modal-page'
import { ImageSizePreset } from 'common_lib/types'
import { generateImageUrl } from 'common_lib/utils'
import { loadBadgeGroup } from '../../badges/queries'
import { useLiveQuery } from 'dexie-react-hooks'
import db from 'mobile_web/modules/db'
import BadgeModalComponent from 'mobile_web/modules/badges/components/BadgeModal'
import { replaceLanguageCodeInPathname } from 'mobile_web/modules/navigation/utils'
import FavouritesRoute from 'mobile_web/modules/favourites/routes/FavouritesRoute'
import { mapboxSetSupportedLanguages } from '../../map/mapboxManager'

const Route = Sentry.withSentryRouting(ReactRouterDomRoute);

interface TabEvent {
	detail: {
		tab: string
	}
}

interface Params {
	tabId: string
}

type Props = Container.Props &
	Container.Actions &
	ReadyGateContainer.Actions &
	RouteComponentProps<Params> &
	MediaQueryProps<'isSmallMobile' | 'isMobile' | 'isBigMobile'>

const Home: React.FC<Props> = props => {
	const [tabs, setTabs] = useState<TabConfig[]>([])
	const [tabsSlugs, setTabsSlugs] = useState<string[]>([])
	const [loading, setLoading] = useState<boolean>(true)
	const [codeLock, setCodeLock] = useState<boolean>(null)
	const [hasWebPrice, setHasWebPrice] = useState<boolean>(null)
	const [isPopoverVisible, setPopoverVisible] = useState<boolean>(false)
	const [floorPopoverEvent, setFloorPopoverEvent] = useState<Event>(null)
	const [modalWindows, setModalWindows] = useState<any[]>([])

	const homeRef = useRef<HTMLDivElement>()
	const [activeTab, setActiveTab] = useState<string>()

	const prevPropsSelectedLanguage: Language = usePrevious(props.selectedLanguage)
	const prevPropsLockApp: CodeStatus = usePrevious(props.lockApp)
	const prevPropsAppMetaData: AppMetaData = usePrevious(props.appMetaData)

	const badgeGroups = useLiveQuery(() => db.badgeGroups.toArray())

	useEffect(() => {
		const { appMetaData, selectedLanguage } = props

		validateApp()

		setLoading(false)

		SettingsService.appLanguage.setPrimaryLocale(appMetaData.primary_language)
		SettingsService.appLanguage.saveSupportedLanguages(appMetaData.supported_languages)
		mapboxSetSupportedLanguages(appMetaData.supported_languages)

		createTabConfig(appMetaData, selectedLanguage)

		window.addEventListener('resize', handleResize)

		if (isSafariBrowser()) {
			document.body.classList.add('uses-safari')
		}
		if (isIOSSafariBrowser()) {
			document.body.classList.add('uses-ios-safari')
		}

		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [])

	useEffect(() => {
		if (props.appMetaData.badge_linked && props.selectedLanguage && props.appManifest) {
			props.appManifest?.badge_groups?.map(async badgeGroup => {
				await loadBadgeGroup(
					badgeGroup?.id,
					badgeGroup?.version,
					badgeGroup?.account_id,
					props.selectedLanguage?.code,
					badgeGroups?.find(g => g.id === badgeGroup?.id)?.version !== badgeGroup?.version,
				)
			})
		}
	}, [props.selectedLanguage, props.appMetaData, props.appManifest])

	useEffect(() => {
		const { location } = props
		const searchParams = new URLSearchParams(location.search)
		const modalWindowsParams = searchParams.getAll('m')

		const modalWindowsParamsArray = []
		let shouldUpdateParams = false

		if (modalWindowsParams.length > 0) {
			modalWindowsParams.forEach((param, index, object) => {
				const paramArray = param.split(';') // ex: {type}_{subtype};{id}

				// check if id in url has broken
				if (!isNaN(Number(paramArray[1]))) {
					modalWindowsParamsArray.push({
						subtype: paramArray[0],
						itemId: paramArray[1],
						itemVersion: paramArray[2],
						accountId: paramArray[3],
					})
				} else {
					// if id is invalid - remove from params
					shouldUpdateParams = true
					object.splice(index, 1)
				}
			})
		}

		// check if the search params should be updated
		if (shouldUpdateParams) {
			searchParams.delete('m')
			modalWindowsParams.forEach(param => searchParams.append('m', param))

			const query = searchParams.toString()

			ionHistory.replace(location.pathname + (query ? '?' + query : '') + location.hash)
		}

		setModalWindows(modalWindowsParamsArray)
	}, [props.location.search])

	useEffect(() => {
		if (codeLock === null) {
			setCodeLock(props.appMetaData.code_lock)
		}
	}, [props.appMetaData, codeLock])

	useEffect(() => {
		if (hasWebPrice === null) {
			setHasWebPrice(!!props.appMetaData.pricing?.web_price?.amount)
		}
	}, [props.appMetaData, hasWebPrice])

	useEffect(() => {
		const { appMetaData, selectedLanguage } = props

		if (selectedLanguage?.code && !appMetaData.supported_languages.includes(selectedLanguage.code as LanguageCode)) {
			setLoading(true)
			ionHistory.replace(replaceLanguageCodeInPathname(appMetaData.supported_languages, appMetaData.primary_language))
			props.selectLanguage(convertLanguage(appMetaData.primary_language))
			SettingsService.appLanguage.setCurrentLocale(appMetaData.primary_language)
			SettingsService.appLanguage.saveSupportedLanguages(appMetaData.supported_languages)
			mapboxSetSupportedLanguages(appMetaData.supported_languages)
			SettingsService.appLanguage.setPrimaryLocale(appMetaData.primary_language)
			setTimeout(() => {
				setLoading(false)
			}, 500)
		} else if (
			(!prevPropsSelectedLanguage && selectedLanguage) ||
			(selectedLanguage && prevPropsSelectedLanguage && selectedLanguage.code !== prevPropsSelectedLanguage.code) ||
			(selectedLanguage && compareTabsVersion())
		) {
			SettingsService.appLanguage.setCurrentLocale(selectedLanguage.code)
			createTabConfig(appMetaData, selectedLanguage)
		}
	}, [props.appMetaData, props.selectedLanguage])

	useEffect(() => {
		const { appMetaData, selectedLanguage } = props
		const {
			app_name: appName,
			default_name: defaultName,
			icon_media_item: iconMediaItem,
			primary_language: primaryLanguage,
		} = appMetaData
		const lang = selectedLanguage?.code || primaryLanguage
		if (appName || defaultName || iconMediaItem) {
			const metaApplicationName = document.createElement('meta')
			const metaAppleAppTitle = document.createElement('meta')
			const metaAppleTouchIcon = document.createElement('meta')
			metaApplicationName.setAttribute('name', 'application-name')
			metaApplicationName.setAttribute('content', appName[lang] || defaultName)
			metaAppleAppTitle.setAttribute('name', 'apple-mobile-web-app-title')
			metaAppleAppTitle.setAttribute('content', appName[lang] || defaultName)
			metaAppleTouchIcon.setAttribute('name', 'apple-touch-icon')
			metaAppleTouchIcon.setAttribute('content', iconMediaItem && generateImageUrl(iconMediaItem.file, { imageSizePreset: ImageSizePreset.Thumbnail }))
			document.getElementsByTagName('head')[0].appendChild(metaApplicationName)
			document.getElementsByTagName('head')[0].appendChild(metaAppleAppTitle)
			document.getElementsByTagName('head')[0].appendChild(metaAppleTouchIcon)
		}
	}, [])

	useEffect(() => {
		const { lockApp, validateCode } = props
		if (!lockApp.loading && lockApp.loaded) {
			if (lockApp.status === 'valid' && !prevPropsLockApp.status) {
				setCodeLock(false)
				setHasWebPrice(false)
			} else if ((lockApp.status === 'invalid' || lockApp.status === 'expired') && !prevPropsLockApp.status) {
				validateAppCode(validateCode)
			}
		}
	}, [props.lockApp])

	const validateApp = async () => {
		await validateAppCode(props.validateCode)
	}

	const handleResize = () => {
		if (isPopoverVisible) {
			togglePopover()
		}
	}

	const compareTabsVersion = (): boolean => {
		let notEqual = false

		const prevTabs = prevPropsAppMetaData.tabs as AppMetaDataTab[]
		const currentTabs = props.appMetaData.tabs as AppMetaDataTab[]

		if (prevTabs && currentTabs && prevTabs.length !== currentTabs.length) {
			return true
		}
		prevTabs.forEach((item: AppMetaDataTab, index) => {
			const tab: AppMetaDataTab = currentTabs[index]
			if (item.version !== tab.version || item.icon !== tab.icon) {
				notEqual = true
				return
			}
		})

		return notEqual
	}

	const createTabConfig = (metaData: AppMetaData, selectedLanguage?: Language) => {
		const {
			location: { pathname },
		} = props
		const tabs = convertMetaDataToTabConfig(metaData, selectedLanguage)
		const tabsSlugs = tabs.map((_tab: TabConfig, index: number): string => `${index + 1}`)

		setTabs(tabs)
		setTabsSlugs(tabsSlugs)

		NavigationService.navigationStack.setTabs(tabs, pathname)
	}

	const showScanner = useMemo<boolean>(() => {
		const { keypad_enabled: keypadEnabled, qr_enabled: qrEnabled, search_enabled: searchEnabled } = props.appMetaData

		return keypadEnabled || qrEnabled || searchEnabled
	}, [props.appMetaData])

	const ionPageWrap = (component: JSX.Element): JSX.Element => <IonPage>{component}</IonPage>

	const tabsStackAction = (routeProps: RouteComponentProps): JSX.Element => {
		let content = undefined

		if (routeProps) {
			const { params } = routeProps.match
			const { tabIndex } = params as RouteProps
			const tab: TabConfig = tabIndex ? getTabByIndex(tabIndex, tabs) : firstTab

			if (tab) {
				const { id, version } = getTabParams(tab)
				content = getPageComponent(tab.subtype, id, version, undefined, undefined, tab.accountId)
			}
		}

		return ionPageWrap(content)
	}

	const basePathAction = (pouteProps: RouteComponentProps): JSX.Element => {
		if (pouteProps) {
			const { locale } = pouteProps.match.params as RouteProps
			saveDefaultLanguageConfig(locale, props.appMetaData)
		}

		if (firstTab) {
			const { id, version } = getTabParams(firstTab)
			return ionPageWrap(getPageComponent(firstTab.subtype, id, version))
		}
	}

	const firstTab = useMemo<TabConfig>(() => (tabs?.length > 0 ? tabs[0] : undefined), [tabs])

	const handleActiveTabClick = useCallback(() => {
		const location = props.location
		const searchParams = new URLSearchParams(location.search)
		if (searchParams.has('m')) {
			setModalWindows([])
			searchParams.delete('m')
			const query = searchParams.toString()
			ionHistory.replace(location.pathname + (query ? '?' + query : '') + location.hash, { modalOpen: false })
		}
	}, [props.location])

	useEffect(() => {
		if (activeTab) {
			setTimeout(() => {
				const tabButton = document?.querySelector(`[tab="${activeTab}"]`)
				if (tabButton) {
					tabButton?.addEventListener('click', handleActiveTabClick)
				}
			}, 0)
		}

		return () => {
			const tabButton = document?.querySelector(`[tab="${activeTab}"]`)
			tabButton?.removeEventListener('click', handleActiveTabClick)
		}
	}, [handleActiveTabClick, activeTab, props.location])

	const tabsChangeAction = (event: TabEvent) => {
		homeRef?.current?.classList?.remove('base-page')

		const tabButton = document?.querySelector('.tab-selected')

		tabButton?.removeEventListener('click', handleActiveTabClick)

		if (event?.detail?.tab) {
			if (event.detail.tab === 'more') {
				const ev: any = {
					target: {
						getBoundingClientRect: () => ({
							left: getPopoverPositionX(),
							top: getPopoverPositionY(),
							height: 0,
						}),
					},
				}

				togglePopover(ev)
			} else {
				NavigationService.navigationStack.setActiveTabIndex(event.detail.tab)
			}
		}
	}

	const tabsChangedAction = (event: TabEvent) => {
		if (event?.detail?.tab) {
			checkAndremoveFullWidthClass()
		}

		const tabButton = document?.querySelector('.tab-selected')

		tabButton?.addEventListener('click', handleActiveTabClick)
	}

	const getTabParams = (tab: TabConfig) => {
		let params = null
		switch (tab.subtype) {
			case COLLECTION_ITEM_SUBTYPE_LIST:
			case COLLECTION_ITEM_SUBTYPE_TOUR:
			case COLLECTION_ITEM_SUBTYPE_MENU: {
				params = tab.params as CollectionListNavigationParams
				return {
					id: params.collectionId,
					version: params.collectionVersion,
				}
			}
			case COLLECTION_ITEM_SUBTYPE_STORY:
			case COLLECTION_ITEM_SUBTYPE_WEB:
			case COLLECTION_ITEM_SUBTYPE_PANORAMA:
				params = tab.params as ScreenNavigationParams
				return {
					id: params.itemId,
					version: params.itemVersion,
				}
			default:
				return undefined
		}
	}

	const getPopoverPositionX = (): number | null => {
		const moreButtonPosition = document.getElementById('tab-button-more')?.getBoundingClientRect()
		const popoverMoreWidth = 0

		if (moreButtonPosition) {
			const correctPossition = window.innerWidth > 409 ? 0 : 14
			const defaultPosition = window.innerWidth - (window.innerWidth - moreButtonPosition.x)
			const popoverPositionRight = isRTL() ? moreButtonPosition.right : defaultPosition + correctPossition
			return isRTL() ? popoverPositionRight : popoverPositionRight - popoverMoreWidth
		}

		return null
	}

	const getPopoverPositionY = (): number | null => {
		const moreButtonPosition = document.getElementById('tab-button-more')?.getBoundingClientRect()
		if (moreButtonPosition) {
			const horizontalPadding = 10
			return moreButtonPosition.y - horizontalPadding
		}
		return null
	}

	const togglePopover = (event?: Event) => {
		setFloorPopoverEvent(event || null)
		setPopoverVisible(Boolean(event))
	}

	const renderTabs = (): JSX.Element => {
		const {
			appMetaData: {
				keypad_enabled: keypadEnabled,
				qr_enabled: qrEnabled,
				search_enabled: searchEnabled,
				badge_linked: badgeLinked,
				badges_on_profile_enabled: badgesOnProfileEnabled,
				favorites_enabled: favoritesEnabled },
			isSmallMobile,
			isMobile,
			isBigMobile,
		} = props

		const scannerTab = (
			<IonTabButton
				key={SCANNER_PAGE_SLUG}
				tab={SCANNER_PAGE_SLUG}
				href={getScannerUrl()}
				className="tab-button"
				data-cy="tab-button-scanner"
			>
				<ScannerIcon keypadEnabled={keypadEnabled} qrEnabled={qrEnabled} searchEnabled={searchEnabled} className="tab-icon" />
				<IonLabel>{getScannerLabel({ keypadEnabled, qrEnabled, searchEnabled })}</IonLabel>
			</IonTabButton>
		)

		const settingsTab = (
			<IonTabButton
				key={SETTINGS_PAGE_SLUG}
				tab={SETTINGS_PAGE_SLUG}
				href={getSettingsUrl()}
				className="tab-button"
				data-cy="tab-button-settings"
			>
				<TabIcon tabBarIcon={'gear'} />
				<IonLabel>{strings.settings}</IonLabel>
			</IonTabButton>
		)

		const profileTab = (
			<IonTabButton
				key={PROFILE_PAGE_SLUG}
				tab={PROFILE_PAGE_SLUG}
				href={getProfileUrl()}
				className="tab-button"
				data-cy="tab-button-profile"
			>
				<TabIcon tabBarIcon={'user'} />
				<IonLabel>{strings.profile}</IonLabel>
			</IonTabButton>
		)

		const tabsToRender = [...tabs]

		if (showScanner) {
			console.log('showScanner')
			tabsToRender.push({
				key: SCANNER_PAGE_SLUG,
				tabId: null,
				tabBarTitle: '',
				tabBarIcon: null,
				type: null,
				subtype: null,
				params: null,
			})
		}

		tabsToRender.push({
			key: (badgeLinked && badgesOnProfileEnabled) || favoritesEnabled ? PROFILE_PAGE_SLUG : SETTINGS_PAGE_SLUG,
			tabId: null,
			tabBarTitle: '',
			tabBarIcon: null,
			type: null,
			subtype: null,
			params: null,
		})

		const moreTab = (
			<IonTabButton tab={MORE_TAB_SLUG} className="tab-button tab-button-more" data-cy="tab-button-more">
				<TabIcon tabBarIcon={'ellipsis-v'} />
				<MorePopover
					keypadEnabled={keypadEnabled}
					qrEnabled={qrEnabled}
					searchEnabled={searchEnabled}
					togglePopover={togglePopover}
					isSmallMobile={isSmallMobile}
					isMobile={isMobile}
					isBigMobile={isBigMobile}
					tabs={tabsToRender}
					showScanner={showScanner}
					isPopoverVisible={isPopoverVisible}
					floorPopoverEvent={floorPopoverEvent}
				/>
			</IonTabButton>
		)

		const tabsLength = tabsCount(tabsToRender.length, {
			isSmallMobile,
			isMobile,
			isBigMobile,
		})

		return (
			<IonTabBar
				onIonTabsWillChange={tabsChangeAction}
				onIonTabsDidChange={tabsChangedAction}
				slot="bottom"
				className={classNames('home-tab-bar', { hidden: isPageInsideIFrame() })}
				dir={getDirMode()}
			>
				{tabsToRender.slice(0, tabsLength).map((tab: TabConfig, index: number) => {
					if (tab.key === SCANNER_PAGE_SLUG) {
						return scannerTab
					} else if (tab.key === SETTINGS_PAGE_SLUG) {
						return settingsTab
					} else if (tab.key === PROFILE_PAGE_SLUG) {
						return profileTab
					}

					return (
						<IonTabButton
							key={index}
							tab={getTabIndex(index)}
							href={formatLanguageUrl(`/${getTabIndex(index)}`)}
							className="tab-button"
							data-cy={`tab-button-${tab.key}`}
						>
							<TabIcon prefix={tab.isCustomIcon ? 'fak' : 'fal'} tabBarIcon={tab.tabBarIcon} />
							<IonLabel>{tab.tabBarTitle || '_'}</IonLabel>
						</IonTabButton>
					)
				})}
				{tabsToRender.length > tabsLength && moreTab}
			</IonTabBar>
		)
	}

	const renderTadIdsRedirect = (
		params: RouteComponentProps<{
			locale?: string
			tabId?: string
		}>,
	): React.ReactElement => {
		const {
			match: {
				params: { locale, tabId },
			},
			location: { search },
		} = params

		const tabIndex = tabs.findIndex(tab => String(tab.tabId) === String(tabId))
		return <Redirect to={formatTabsUrl(locale, tabIndex + 1) + search} />
	}

	const renderMainRoutes = (dirMode: 'rtl' | 'ltr', tabsSlugs: string[]): JSX.Element => (
		<IonRouterOutlet id="mainMenu" dir={dirMode}>
			<Route path={getTabsStackRoutePath(tabsSlugs)} render={tabsStackAction} exact={true} />
			<Route path={getTabsIDsStackRoutePath(tabs, tabsSlugs)} render={renderTadIdsRedirect} exact={true} />
			<Route path={get404RoutePath(tabsSlugs)} render={() => ionPageWrap(<Page404 />)} exact={true} />

			<Route
				path={getScannerStackRoutePath()}
				render={params => (showScanner ? ionPageWrap(<ScannerRoute {...params} />) : <Redirect to={BASE_PATH} />)}
				exact={true}
			/>

			<Route
				path={getSettingsStackRoutePath()}
				render={params => ionPageWrap(<SettingsRoute {...params} />)}
				exact={true}
			/>

			<Route
				path={getProfileStackRoutePath()}
				render={params => ionPageWrap(<ProfileRoute {...params} />)}
				exact={true}
			/>

			<Route
				path={getBadgesStackRoutePath()}
				render={params => ionPageWrap(<BadgesRoute {...params} />)}
				exact={true}
			/>

			<Route
				path={getFavouritesStackRoutePath()}
				render={params => ionPageWrap(<FavouritesRoute {...params} />)}
				exact={true}
			/>

			<Route
				path={getCollectionListRoutePath(tabsSlugs)}
				render={params => ionPageWrap(<CollectionListRoute {...params} />)}
				exact={true}
			/>

			<Route
				path={getCollectionMenuRoutePath(tabsSlugs)}
				render={params => ionPageWrap(<CollectionListRoute {...params} />)}
				exact={true}
			/>

			<Route
				path={getCollectionTourRoutePath(tabsSlugs)}
				render={params => ionPageWrap(<CollectionTourRoute {...params} />)}
				exact={true}
			/>

			<Route
				path={getScreenWebRoutePath(tabsSlugs)}
				render={params => ionPageWrap(<ScreenWebRoute {...params} />)}
				exact={true}
			/>

			<Route path={getScreenStoryRoutePath(tabsSlugs)} component={ScreenStoryRoute} exact={true} />

			<Route path={getScreenPanoramaRoutePath(tabsSlugs)} component={ScreenPanoramaRoute} exact={true} />

			<Route path={getBasePath()} render={basePathAction} exact={true} />

			<Route
				path={getCollectionItemsRoutePath(tabsSlugs, true)}
				render={params => ionPageWrap(<CollectionItemsRoute {...params} />)}
				exact={true}
			/>
		</IonRouterOutlet>
	)

	const renderTabsRoutes = (dirMode: 'rtl' | 'ltr', tabsSlugs: string[]): JSX.Element => (
		<IonTabs className="home-tabs" onIonTabsDidChange={e => setActiveTab(e.detail.tab)}>
			{renderMainRoutes(dirMode, tabsSlugs)}
			{renderTabs()}
		</IonTabs>
	)

	if (loading || !tabs?.length) {
		return <DefaultPagePlaceholder />
	}

	const {
		selectedLanguage,
		appMetaData: {
			id: appId,
			primary_language: primaryLanguage,
			default_name: defaultName,
			app_name: appName,
			supported_languages: supportedLanguages,
			code_title: codeTitle,
			code_text: codeText,
			code_background_media_item: codeBackgroundMediaItem,
			pricing,
		},
		lockApp,
	} = props

	const lang = selectedLanguage?.code || primaryLanguage

	const dirMode = getDirMode()

	const { pathname } = ionHistory.location

	const schema = {
		type: 'application/ld+json',
		innerHTML: JSON.stringify({
			'@type': 'SoftwareApplication',
			name: appName[lang] || defaultName,
			applicationCategory: [
				'TravelApplication',
				'EducationalApplication',
				'LifestyleApplication',
				'BrowserApplication',
				'EntertainmentApplication',
				'MultimediaApplication',
			],
			countriesSupported: supportedLanguages,
			offers: {
				'@type': 'Offer',
				price: '0',
			},
		}),
	}

	const show404 = !matchPaths(ionHistory.location.pathname, tabs, tabsSlugs)

	return (
		<div
			id="homeContainer"
			ref={homeRef}
			className={classNames(`container-${dirMode}`, 'home-container', {
				'base-page':
					pathname === '/' || pathname === BASE_PATH || (selectedLanguage && pathname === `/${selectedLanguage.code}`),
			})}
		>
			<Helmet
				htmlAttributes={{ lang }}
				defaultTitle={appName[lang] || defaultName || ''}
				titleTemplate={appName[lang] || defaultName ? `%s - ${appName[lang] || defaultName}` : ''}
				script={[schema]}
			/>

			{lockApp.loading ? (
				<></>
			) : (codeLock || hasWebPrice) ? (
				<Route
					path="*"
					render={params =>
						ionPageWrap(
							<LockAppRoute
								title={codeTitle}
								description={codeText}
								backgroundImage={codeBackgroundMediaItem}
								linkedId={appId}
								linkedType={RedeemCode.LinkedTypeEnum.App}
								webPrice={hasWebPrice && pricing.web_price}
								{...params}
							/>,
						)
					}
					exact={true}
				/>
			) : (
				<>
					{renderTabsRoutes(dirMode, tabsSlugs)}
					<Route
						path={getCollectionItemsRoutePath(tabsSlugs, false)}
						render={params => ionPageWrap(<CollectionItemsRoute {...params} />)}
						exact={true}
					/>
					{show404 && <Redirect exact from="*" to={get404Url()} />}
					{modalWindows.map((modal, index) => (
						<ModalPage
							key={index}
							loading={modal.loading}
							subtype={modal.subtype}
							itemId={Number(modal.itemId)}
							itemVersion={Number(modal.itemVersion)}
							accountId={modal.accountId && Number(modal.accountId)}
							showModal={true}
							toggleModal={() => {
								NavigationService.popModalWindow(modal.subtype, Number(modal.itemId))
							}}
							className={`section-modal-${modal.subtype.split('_').reverse()[0]}`}
						/>
					))}
					<BadgeModalComponent />
				</>
			)}
		</div>
	)
}

const mediaQueries: UseMediaArgs = {
	isSmallMobile: [{ maxWidth: 319 }],
	isMobile: [{ maxWidth: 411, minWidth: 320 }],
	isBigMobile: [{ maxWidth: 639, minWidth: 412 }],
}

const HomeComponent = Container.default(WithMediaQuery(mediaQueries)(withRouter(Home)))
export default HomeComponent
